/** Small building blocks shared by the Command Center pages. */

import React, { useCallback, useEffect, useRef, useState } from "react";
import { Navigate, useParams } from "react-router-dom";
import { DatePicker } from "antd";
import ReactECharts from "echarts-for-react";
import dayjs from "dayjs";
import { CC_CSS } from "./ccStyles";
import { CcMode, CcTest, CheckResult, CheckStage, Engine, LiveJob, ccApi, isCcMode } from "./api";
import { LEVEL, Level, TONE, Tone, decisionText } from "./common";

export const CcStyles: React.FC = () => <style>{CC_CSS}</style>;

export const Badge: React.FC<{ tone: Tone; children: React.ReactNode }> = ({ tone, children }) => (
  <span className="cc-badge" style={{ color: TONE[tone].color, background: TONE[tone].bg }}>
    <span className="cc-dot" />
    {children}
  </span>
);

export const LevelBadge: React.FC<{ level: Level; style?: React.CSSProperties }> = ({ level, style }) => (
  <span className="cc-badge" style={{ color: LEVEL[level].color, background: LEVEL[level].bg, ...style }}>
    <span className="cc-dot" />
    {LEVEL[level].t}
  </span>
);

export const Segments: React.FC<{ done: number; cur: boolean }> = ({ done, cur }) => (
  <div style={{ display: "flex", gap: 3 }}>
    {Array.from({ length: 5 }, (_, i) => (
      <span key={i} className={`cc-seg${i < done ? " d" : i === done && cur ? " c" : ""}`} />
    ))}
  </div>
);

export const StageBar: React.FC<{ labels: string[]; stage: number; maxStage: number; onGo: (n: number) => void }> = ({ labels, stage, maxStage, onGo }) => (
  <nav className="cc-stages" aria-label="Test stages">
    {labels.map((label, i) => {
      const n = i + 1;
      const cur = n === stage;
      const locked = n > maxStage;
      return (
        <button key={label} className={`cc-stg${cur ? " cur" : ""}${locked ? " locked" : ""}`} onClick={() => !locked && onGo(n)} disabled={locked}>
          <span className="n">{!cur && !locked ? "✓" : n}</span>
          {label}
        </button>
      );
    })}
  </nav>
);

/** Budget in $K per week (or month): a text field (digits, optional commas, decimals), saved as a number when it's above 0. */
export const BudgetInput: React.FC<{ value: number; onChange: (k: number) => void; ariaLabel?: string; per?: "week" | "month" }> = ({ value, onChange, ariaLabel, per = "week" }) => {
  const [text, setText] = useState(String(value ?? ""));
  useEffect(() => { setText(prev => (Number(prev.replace(/,/g, "")) === value ? prev : String(value ?? ""))); }, [value]);
  const n = Number(text.replace(/,/g, ""));
  const invalid = text.trim() === "" || !isFinite(n) || n <= 0;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ fontSize: 14, color: "var(--text-secondary)" }}>$</span>
        <input className="cc-input" type="text" inputMode="decimal" autoComplete="off" value={text}
          style={{ width: 140, borderColor: invalid ? "var(--error)" : undefined }} aria-label={ariaLabel || `Spend per ${per} in thousands of dollars`}
          onChange={e => {
            const v = e.target.value;
            if (!/^[\d,]*\.?\d*$/.test(v)) return; // digits, commas, one decimal point
            setText(v);
            const num = Number(v.replace(/,/g, ""));
            if (v.trim() !== "" && isFinite(num) && num > 0) onChange(num);
          }} />
        <span style={{ fontSize: 13, color: "var(--text-secondary)" }}>K per {per}</span>
        {!invalid && <span className="cc-help">= ${Math.round(n * 1000).toLocaleString()} / {per}</span>}
      </div>
      {invalid && <span style={{ fontSize: 12, color: "var(--error)" }}>Enter an amount above 0.</span>}
    </div>
  );
};

/** Test length in whole weeks: a text field (digits only), saved as a number when it's between min and max. */
export const WeeksInput: React.FC<{ value?: number; onChange: (w: number) => void; ariaLabel: string; min?: number; max?: number }> = ({ value, onChange, ariaLabel, min = 1, max = 52 }) => {
  const [text, setText] = useState(value ? String(value) : "");
  useEffect(() => { setText(prev => (Number(prev) === value ? prev : value ? String(value) : "")); }, [value]);
  const n = Number(text);
  const ok = text.trim() !== "" && Number.isInteger(n) && n >= min && n <= max;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <input className="cc-input" type="text" inputMode="numeric" autoComplete="off" value={text}
          style={{ width: 90, borderColor: ok ? undefined : "var(--error)" }} aria-label={ariaLabel}
          onChange={e => {
            const v = e.target.value;
            if (!/^\d*$/.test(v)) return; // whole weeks only
            setText(v);
            const num = Number(v);
            if (v !== "" && num >= min && num <= max) onChange(num);
          }} />
        <span style={{ fontSize: 13, color: "var(--text-secondary)" }}>weeks</span>
      </div>
      {!ok && <span style={{ fontSize: 12, color: "var(--error)" }}>Enter a whole number of weeks from {min} to {max}.</span>}
    </div>
  );
};

/** Average monthly BAU media spend: a text field in full dollars, like the GeoLift study's
 * "Average Monthly BAU Media Spent" (digits, optional commas, up to 2 decimals). Value is in $K. */
export const BauInput: React.FC<{ valueK?: number; onChange: (k: number | undefined) => void; ariaLabel?: string; disabled?: boolean }> = ({ valueK, onChange, ariaLabel, disabled }) => {
  const toText = (k?: number) => (k ? String(Math.round(k * 1000 * 100) / 100) : "");
  const [text, setText] = useState(toText(valueK));
  useEffect(() => {
    setText(prev => (Number(prev.replace(/,/g, "")) === Math.round((valueK || 0) * 1000 * 100) / 100 ? prev : toText(valueK)));
  }, [valueK]);
  const n = Number(text.replace(/,/g, ""));
  const ok = text.trim() !== "" && isFinite(n) && n > 0;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <span style={{ fontSize: 14, color: "var(--text-secondary)" }}>$</span>
        <input className="cc-input" type="text" inputMode="decimal" autoComplete="off" value={text} disabled={disabled}
          placeholder="e.g. 2000.50" style={{ width: 170, borderColor: text && !ok ? "var(--error)" : undefined }}
          aria-label={ariaLabel || "Average monthly BAU media spend in dollars"}
          onChange={e => {
            const v = e.target.value;
            if (!/^[\d,]*\.?\d{0,2}$/.test(v)) return; // digits, commas, up to 2 decimals
            setText(v);
            const num = Number(v.replace(/,/g, ""));
            onChange(v.trim() !== "" && isFinite(num) && num > 0 ? num / 1000 : undefined);
          }} />
        <span style={{ fontSize: 13, color: "var(--text-secondary)" }}>per month</span>
        {ok && <span className="cc-help">= ${n.toLocaleString(undefined, { maximumFractionDigits: 2 })} / month</span>}
      </div>
      {text && !ok && <span style={{ fontSize: 12, color: "var(--error)" }}>Enter an amount above 0.</span>}
    </div>
  );
};

/** An amount in full dollars (digits, optional commas, up to 2 decimals), sent exactly as typed. */
export const DollarInput: React.FC<{ value?: number; onChange: (v: number | undefined) => void; per: string; placeholder?: string; ariaLabel: string; disabled?: boolean }> = ({ value, onChange, per, placeholder, ariaLabel, disabled }) => {
  const [text, setText] = useState(value ? String(value) : "");
  useEffect(() => { setText(prev => (Number(prev.replace(/,/g, "")) === (value || 0) ? prev : value ? String(value) : "")); }, [value]);
  const n = Number(text.replace(/,/g, ""));
  const ok = text.trim() !== "" && isFinite(n) && n > 0;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <span style={{ fontSize: 14, color: "var(--text-secondary)" }}>$</span>
        <input className="cc-input" type="text" inputMode="decimal" autoComplete="off" value={text} disabled={disabled}
          placeholder={placeholder} style={{ width: 170, borderColor: text && !ok ? "var(--error)" : undefined }} aria-label={ariaLabel}
          onChange={e => {
            const v = e.target.value;
            if (!/^[\d,]*\.?\d{0,2}$/.test(v)) return;
            setText(v);
            const num = Number(v.replace(/,/g, ""));
            onChange(v.trim() !== "" && isFinite(num) && num > 0 ? num : undefined);
          }} />
        <span style={{ fontSize: 13, color: "var(--text-secondary)" }}>per {per}</span>
        {ok && <span className="cc-help">= ${n.toLocaleString(undefined, { maximumFractionDigits: 2 })} / {per}</span>}
      </div>
      {text && !ok && <span style={{ fontSize: 12, color: "var(--error)" }}>Enter an amount above 0.</span>}
    </div>
  );
};

/** GeoLift's "Statistical Significance Threshold %": a whole or decimal percent from 50 to 100.
 * Reports undefined while the text isn't a valid threshold. */
export const StatSigInput: React.FC<{ value: number; onChange: (v: number | undefined) => void; disabled?: boolean }> = ({ value, onChange, disabled }) => {
  const [text, setText] = useState(String(value));
  useEffect(() => { setText(prev => (Number(prev) === value ? prev : String(value))); }, [value]);
  const n = Number(text);
  const ok = text.trim() !== "" && isFinite(n) && n >= 50 && n <= 100;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <input className="cc-input" type="text" inputMode="decimal" autoComplete="off" value={text} disabled={disabled}
          placeholder="50 to 100" style={{ width: 110, borderColor: ok ? undefined : "var(--error)" }}
          aria-label="Statistical significance threshold in percent, 50 to 100"
          onChange={e => {
            const v = e.target.value;
            if (!/^\d{0,3}(\.\d{0,2})?$/.test(v)) return;
            setText(v);
            const num = Number(v);
            onChange(v.trim() !== "" && isFinite(num) && num >= 50 && num <= 100 ? num : undefined);
          }} />
        <span style={{ fontSize: 13, color: "var(--text-secondary)" }}>%</span>
      </div>
      {!ok && <span style={{ fontSize: 12, color: "var(--error)" }}>Enter a value from 50 to 100.</span>}
    </div>
  );
};

/** Candidate test lengths, like the RCT Experiment's Setup: type 4-8 and press Enter, up to 5,
 * kept in the order typed (the engine's random trials are seeded by each duration's position). */
export const DurationsInput: React.FC<{ value: number[]; onChange: (v: number[]) => void; disabled?: boolean }> = ({ value, onChange, disabled }) => {
  const [text, setText] = useState("");
  const [error, setError] = useState<string | null>(null);
  const full = value.length >= 5;
  const add = () => {
    const n = parseInt(text.trim(), 10);
    if (!text.trim()) return;
    if (!Number.isInteger(n) || n < 4 || n > 8) { setError("Enter a duration between 4 and 8 weeks."); return; }
    setError(null); setText("");
    if (value.includes(n) || full) return;
    onChange([...value, n]);
  };
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        {value.map(w => (
          <span key={w} className="cc-pill on" style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            {w} weeks
            {!disabled && <button type="button" aria-label={`Remove ${w} weeks`} onClick={() => onChange(value.filter(x => x !== w))}
              style={{ background: "none", border: 0, padding: 0, cursor: "pointer", color: "inherit", fontSize: 14, lineHeight: 1 }}>×</button>}
          </span>
        ))}
        <input className="cc-input" type="text" inputMode="numeric" autoComplete="off" value={text} disabled={disabled || full}
          placeholder={full ? "Maximum 5 durations" : "Weeks (4-8), Enter"} style={{ width: 170 }} aria-label="Add a candidate test duration in weeks"
          onChange={e => { if (/^\d{0,2}$/.test(e.target.value)) { setText(e.target.value); setError(null); } }}
          onKeyDown={e => {
            if (e.key === "Enter" || e.key === ",") { e.preventDefault(); add(); }
            else if (e.key === "Backspace" && text === "" && value.length) onChange(value.slice(0, -1));
          }}
          onBlur={add} />
      </div>
      {error && <span style={{ fontSize: 12, color: "var(--error)" }}>{error}</span>}
    </div>
  );
};

/** Plan question: the decision this test answers, in the user's own words. */
export const DecisionField: React.FC<{ value: string; onChange: (v: string) => void }> = ({ value, onChange }) => {
  const [text, setText] = useState(decisionText(value));
  useEffect(() => { setText(prev => (prev.trim() === decisionText(value) ? prev : decisionText(value))); }, [value]);
  return (
    <div className="cc-card">
      <label className="cc-qh" htmlFor="cc-decision"><span>What decision will this test answer?</span></label>
      <input id="cc-decision" className="cc-input" maxLength={200} value={text}
        placeholder="e.g. Should we double CTV spend in Q1?"
        onChange={e => { setText(e.target.value); onChange(e.target.value.trim()); }} />
      <span className="cc-help">Write it as a yes/no question the results can answer. It heads the readout and the final decision.</span>
    </div>
  );
};

/** Plan question: campaign start date (same antd picker as the other features; past dates allowed). */
export const StartDateField: React.FC<{ value: string; onChange: (iso: string) => void; note?: string }> = ({ value, onChange, note }) => (
  <div className="cc-card">
    <div className="cc-qh"><span>Earliest the campaign can start</span>{note && <span className="cc-help">{note}</span>}</div>
    <DatePicker size="large" format="YYYY-MM-DD" placeholder="Select the campaign start date" allowClear={false} showToday={false}
      value={value ? dayjs(value) : null} onChange={d => d && onChange(d.format("YYYY-MM-DD"))} style={{ maxWidth: 260, width: "100%" }} />
    <span className="cc-help">Checked against other tests on the Calendar and the US marketing calendar.</span>
  </div>
);

/** Plan question: anything else that moves sales in the test markets during the flight. */
export const ChangeField: React.FC<{ change: "no" | "yes"; note?: string; onChange: (p: { change?: "no" | "yes"; changeNote?: string }) => void }> = ({ change, note, onChange }) => {
  const [text, setText] = useState(note || "");
  useEffect(() => { setText(prev => (prev.trim() === (note || "") ? prev : note || "")); }, [note]);
  const pill = (on: boolean) => `cc-pill${on ? " on" : ""}`;
  return (
    <div className="cc-card">
      <div className="cc-qh"><span>Are other changes planned in the test markets during the test?</span></div>
      <span className="cc-help" style={{ marginTop: -6 }}>For example a promotion or sale, a price change, a new product launch, another ad campaign, or new stores opening.</span>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <button className={pill(change === "no")} onClick={() => onChange({ change: "no" })}>No other changes planned</button>
        <button className={pill(change === "yes")} onClick={() => onChange({ change: "yes" })}>Yes, something else will change</button>
      </div>
      {change === "yes" && (
        <>
          <textarea className="cc-input" rows={3} maxLength={500} value={text} style={{ resize: "vertical" }}
            placeholder="What changes, where and when? e.g. 20% off sale in Texas markets, Mar 3–10"
            onChange={e => { setText(e.target.value); onChange({ changeNote: e.target.value.trim() }); }} />
          <span className="cc-help">Other changes in test markets mix into the measured lift. Move them outside the test window or test markets if you can.</span>
        </>
      )}
    </div>
  );
};

/** Expand/collapse card. The body mounts on first open and then stays mounted, so
 *  heavy content (charts, per-market API calls) loads only when asked for. */
export const CollapseSection: React.FC<{
  title: string; hint?: React.ReactNode; defaultOpen?: boolean; children: React.ReactNode;
}> = ({ title, hint, defaultOpen = false, children }) => {
  const [open, setOpen] = useState(defaultOpen);
  const [mounted, setMounted] = useState(defaultOpen);
  useEffect(() => { if (open) setMounted(true); }, [open]);
  return (
    <div className="cc-card cc-collapse">
      <button className="cc-collapse-head" aria-expanded={open} onClick={() => setOpen(o => !o)}>
        <svg className={`cc-chev${open ? " open" : ""}`} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><path d="M9 6l6 6-6 6" /></svg>
        <span className="cc-qh" style={{ flex: 1 }}>{title}</span>
        {hint && <span className="cc-help">{hint}</span>}
      </button>
      {mounted && <div style={{ display: open ? "flex" : "none", flexDirection: "column", gap: 12 }}>{children}</div>}
    </div>
  );
};

/** Print one element (e.g. the test plan) on its own: a copy goes into a hidden iframe
 *  with the Command Center styles, so nothing else on the page prints and long content
 *  flows over several pages. Elements with .cc-noprint are left out. The PDF name is `title`. */
export function printElement(el: HTMLElement | null, title: string) {
  if (!el) return;
  const esc = (t: string) => t.replace(/[&<>"]/g, c => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c] as string));
  const frame = document.createElement("iframe");
  Object.assign(frame.style, { position: "fixed", right: "0", bottom: "0", width: "0", height: "0", border: "0" });
  document.body.appendChild(frame);
  const win = frame.contentWindow;
  const doc = frame.contentDocument;
  if (!win || !doc) { frame.remove(); return; }
  doc.open();
  doc.write(`<!doctype html><html><head><meta charset="utf-8"><title>${esc(title)}</title><style>
:root{--font-sans:'Geist','Inter',system-ui,-apple-system,sans-serif;--font-mono:'JetBrains Mono','Fira Code',monospace;--border-subtle:#E5E7EB;--radius:10px}
*{box-sizing:border-box}body{margin:0;font-family:var(--font-sans);color:#111827;-webkit-print-color-adjust:exact;print-color-adjust:exact}
${CC_CSS}
.cc-noprint{display:none!important}.cc-paper{border:0!important;padding:0!important}
@page{margin:14mm}
</style></head><body>${el.outerHTML}</body></html>`);
  doc.close();
  const prevTitle = document.title;
  document.title = title;  // some browsers name the PDF after the top page
  const done = () => { document.title = prevTitle; frame.remove(); };
  setTimeout(() => {
    win.focus();
    win.print();  // blocks until the dialog closes in Chrome/Edge
    setTimeout(done, 500);
  }, 250);
}

/** Readout chart: test markets vs their counterfactual, campaign window shaded.
 *  Opens on the recent weeks plus the campaign; the slider shows the full history. */
export const ReadoutChart: React.FC<{
  t: number[]; c: number[]; startIndex: number; dates?: string[]; testLabel: string; ctrlLabel: string; money: boolean;
}> = ({ t, c, startIndex, dates, testLabel, ctrlLabel, money }) => {
  const css = (name: string, fallback: string) =>
    (typeof window !== "undefined" && getComputedStyle(document.documentElement).getPropertyValue(name).trim()) || fallback;
  const blue = css("--blue", "#3B82F6"), muted = css("--text-tertiary", "#64748B"), text = css("--text-secondary", "#94A3B8");
  const grid = css("--border-subtle", "#1E2A3E"), shade = css("--blue-subtle", "#1E3A5F");
  const n = t.length;
  const labels = dates?.length === n ? dates : t.map((_, i) => `Week ${i + 1}`);
  const end = Math.max(0, n - 1);
  const zoomFrom = Math.max(0, startIndex - Math.max(16, (n - startIndex) * 3));
  const compact = (v: number) => {
    const a = Math.abs(v);
    const s = a >= 1e6 ? (v / 1e6).toFixed(1) + "M" : a >= 1e3 ? (v / 1e3).toFixed(0) + "K" : v.toFixed(0);
    return (money ? "$" : "") + s;
  };
  const option = {
    grid: { left: 8, right: 16, top: 28, bottom: 56, containLabel: true },
    tooltip: {
      trigger: "axis",
      valueFormatter: (v: number) => (money ? "$" : "") + Math.round(v).toLocaleString(),
    },
    legend: { top: 0, left: 0, textStyle: { color: text }, data: [testLabel, ctrlLabel] },
    xAxis: { type: "category", data: labels, boundaryGap: false, axisLabel: { color: text }, axisLine: { lineStyle: { color: grid } } },
    yAxis: { type: "value", scale: true, axisLabel: { color: text, formatter: compact }, splitLine: { lineStyle: { color: grid } } },
    dataZoom: [
      { type: "inside", startValue: zoomFrom, endValue: end },
      { type: "slider", startValue: zoomFrom, endValue: end, height: 18, bottom: 8, textStyle: { color: text } },
    ],
    series: [
      {
        name: testLabel, type: "line", data: t, showSymbol: false, lineStyle: { width: 2.5, color: blue }, itemStyle: { color: blue },
        markArea: {
          silent: true, itemStyle: { color: shade, opacity: 0.55 },
          label: { show: true, position: "insideTop", color: text, formatter: "Campaign" },
          data: [[{ xAxis: labels[Math.min(startIndex, end)] }, { xAxis: labels[end] }]],
        },
      },
      { name: ctrlLabel, type: "line", data: c, showSymbol: false, lineStyle: { width: 2, type: "dashed", color: muted }, itemStyle: { color: muted } },
    ],
  };
  return <ReactECharts option={option} notMerge style={{ height: 300, width: "100%" }} opts={{ renderer: "svg" }} />;
};

/** Delete confirmation, same look as the other menus (Experiments, MMM, Bid Optimizer). */
export const DeleteModal: React.FC<{
  name: string; body: string; onConfirm: () => void; onCancel: () => void; loading: boolean;
}> = ({ name, body, onConfirm, onCancel, loading }) => (
  <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }}
    onClick={onCancel}>
    <div role="dialog" aria-modal="true" aria-label={`Delete ${name}`} onClick={e => e.stopPropagation()}
      style={{ background: "var(--bg-surface)", borderRadius: "var(--radius)", padding: "24px 28px", maxWidth: 380, width: "90%", border: "1px solid var(--border-default)" }}>
      <div style={{ fontSize: 15, fontWeight: 600, color: "var(--text-primary)", marginBottom: 8, fontFamily: "var(--font-sans)" }}>
        Delete "{name}"?
      </div>
      <div style={{ fontSize: 13, color: "var(--text-secondary)", marginBottom: 20, fontFamily: "var(--font-sans)", lineHeight: 1.5 }}>{body}</div>
      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
        <button onClick={onCancel} disabled={loading} style={{
          padding: "7px 16px", borderRadius: "var(--radius)", background: "var(--bg-hover)", color: "var(--text-secondary)",
          border: "1px solid var(--border-default)", fontSize: 13, cursor: "pointer", fontFamily: "var(--font-sans)",
        }}>Cancel</button>
        <button onClick={onConfirm} disabled={loading} style={{
          padding: "7px 16px", borderRadius: "var(--radius)", background: "#ef4444", color: "#fff",
          border: "none", fontSize: 13, cursor: "pointer", fontFamily: "var(--font-sans)", fontWeight: 500,
        }}>{loading ? "Deleting…" : "Delete"}</button>
      </div>
    </div>
  </div>
);

export const UploadIcon = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
    <path d="M12 16V4" /><path d="M7 9l5-5 5 5" /><path d="M4 16v3a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-3" />
  </svg>
);

export const ClockIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" />
  </svg>
);

export const Spinner: React.FC<{ size?: number }> = ({ size = 16 }) => (
  <span style={{ width: size, height: size, border: "2px solid var(--border-default)", borderTopColor: "var(--blue)", borderRadius: "50%", display: "inline-block", animation: "ccSpin .8s linear infinite", flexShrink: 0 }} />
);

const elapsed = (from: string | null | undefined, now: number) => {
  if (!from) return "";
  const s = Math.max(0, Math.round((now - new Date(from).getTime()) / 1000));
  return s < 60 ? `${s}s` : `${Math.floor(s / 60)}m ${String(s % 60).padStart(2, "0")}s`;
};

/** Progress of a background design / readout job (server-side; survives leaving the page). */
export const JobProgress: React.FC<{ job?: LiveJob; startedAt?: string; label: string }> = ({ job, startedAt, label }) => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(t); }, []);
  const pct = Math.max(3, Math.min(99, job?.progress ?? 0));
  return (
    <div className="cc-hand" style={{ flexDirection: "column", alignItems: "stretch", gap: 8 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <Spinner />
        <span style={{ flex: 1, fontSize: 13 }}>{job?.message || label}</span>
        <span className="cc-mono" style={{ fontSize: 12, color: "var(--text-secondary)" }}>{elapsed(job?.started_at || startedAt, now)}</span>
      </div>
      <div style={{ height: 6, borderRadius: 3, background: "var(--border-default)", overflow: "hidden" }} role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
        <div style={{ width: `${pct}%`, height: "100%", background: "var(--blue)", transition: "width .6s ease" }} />
      </div>
      <span className="cc-help">Runs on the server. You can leave this page and come back.</span>
    </div>
  );
};

/** Validates the :mode route param; renders children with the mode or redirects. */
export const WithMode: React.FC<{ children: (mode: CcMode) => React.ReactElement }> = ({ children }) => {
  const { mode } = useParams();
  if (!isCcMode(mode)) return <Navigate to="/command-center/ml" replace />;
  return children(mode);
};

export const ErrorBanner: React.FC<{ msg: string | null; onClose: () => void }> = ({ msg, onClose }) =>
  !msg ? null : (
    <div className="cc-note bad" style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
      <span style={{ fontWeight: 600 }}>Error</span>
      <span style={{ flex: 1, wordBreak: "break-word" }}>{msg}</span>
      <button className="cc-icon-btn" onClick={onClose} aria-label="Dismiss">✕</button>
    </div>
  );

export const errMsg = (err: any) => err?.response?.data?.detail || err?.message || "Unknown error";

/**
 * Holds a wizard's state (owned by the UI, saved as it changes) and the
 * engine block (owned by the server, replaced whenever an API call returns
 * the test). `version` bumps after every save so guardrail checks refresh.
 */
export function useSavedTest<S extends { name: string; adv: string; stage: number }>(opts: {
  mode: CcMode;
  testId: string | undefined;
  defaults: S;
  statusOf: (s: S) => { key: string };
  sanitize?: (s: S) => S;
  /** Upgrades a stored state (raw) after it is merged with the defaults. */
  migrate?: (raw: unknown, s: S) => S;
}) {
  const { mode, testId, defaults, statusOf, sanitize, migrate } = opts;
  const [state, setState] = useState<S>(defaults);
  const [engine, setEngine] = useState<Engine>({});
  const [live, setLive] = useState<CcTest["live"]>({});
  const [loading, setLoading] = useState(!!testId);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [version, setVersion] = useState(0);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const unsaved = useRef<S | null>(null);
  const pending = useRef<{ stage: number } | null>(null);
  const loadedId = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (!testId) { setState(defaults); setEngine({}); setLive({}); setLoading(false); loadedId.current = undefined; return; }
    if (loadedId.current === testId) return;
    let cancelled = false;
    setLoading(true);
    ccApi.getTest(testId)
      .then(t => {
        if (cancelled) return;
        const merged = { ...defaults, ...(t.state as Partial<S>), name: t.name, adv: t.advertiser };
        setState(migrate ? migrate(t.state, merged) : merged);
        setEngine(t.engine || {});
        setLive(t.live || {});
        loadedId.current = testId;
      })
      .catch(err => { if (!cancelled) setError(`Failed to load test: ${errMsg(err)}`); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [testId]);

  const persist = useCallback(async (s: S) => {
    if (!testId) return;
    const clean = sanitize ? sanitize(s) : s;
    setSaving(true);
    try {
      await ccApi.updateTest(testId, { stage: clean.stage, status: statusOf(clean).key, state: clean });
      setVersion(v => v + 1);
    } catch (err) {
      setError(`Could not save: ${errMsg(err)}`);
    } finally {
      setSaving(false);
    }
  }, [testId, sanitize, statusOf]);

  /** Merge a change; saves after a short pause (immediately when the stage changes). */
  const update = useCallback((patch: Partial<S> | ((s: S) => Partial<S>)) => {
    setState(prev => {
      if (!pending.current) pending.current = { stage: prev.stage };
      return { ...prev, ...(typeof patch === "function" ? patch(prev) : patch) };
    });
  }, []);

  useEffect(() => {
    if (!pending.current) return;
    const delay = pending.current.stage !== state.stage ? 0 : 500;
    pending.current = null;
    if (timer.current) clearTimeout(timer.current);
    unsaved.current = state;
    timer.current = setTimeout(() => { unsaved.current = null; timer.current = null; persist(state); }, delay);
  }, [state, persist]);

  const persistRef = useRef(persist);
  persistRef.current = persist;
  useEffect(() => () => {
    if (timer.current) clearTimeout(timer.current);
    if (unsaved.current) persistRef.current(unsaved.current);
  }, []);

  /** Take the server's engine block (and live job progress) from a returned test. */
  const applyTest = useCallback((t: CcTest) => { setEngine(t.engine || {}); setLive(t.live || {}); setVersion(v => v + 1); }, []);

  const create = useCallback(async (s: S) => {
    const clean = sanitize ? sanitize(s) : s;
    return ccApi.createTest(mode, {
      name: clean.name.trim(), advertiser: clean.adv.trim(), stage: clean.stage, status: statusOf(clean).key, state: clean,
    });
  }, [mode, sanitize, statusOf]);

  return { state, setState, update, engine, live, applyTest, loading, error, setError, saving, create, version };
}

/** Live guardrail checks for one or more stages, refreshed when `version` changes. */
export function useChecks(testId: string | undefined, version: number) {
  const [checks, setChecks] = useState<CheckResult[]>([]);
  useEffect(() => {
    if (!testId) return;
    let cancelled = false;
    ccApi.checks(testId).then(c => { if (!cancelled) setChecks(c); }).catch(() => {});
    return () => { cancelled = true; };
  }, [testId, version]);
  const forStage = (stage: CheckStage) => checks.filter(c => c.stage === stage);
  const blockers = (stage: CheckStage) => forStage(stage).filter(c => c.status === "fail" && c.level === "block");
  return { checks, forStage, blockers };
}

/** Guardrail list for a stage: one row per rule, colour by level when it fails. */
export const ChecksList: React.FC<{ checks: CheckResult[]; empty?: string; compact?: boolean }> = ({ checks, empty, compact }) => {
  if (!checks.length) return <span className="cc-help">{empty || "No guardrail rules apply to this stage."}</span>;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      {checks.map(c => {
        const failed = c.status === "fail";
        const tone = c.status === "pending" ? { t: "Pending", color: "var(--text-tertiary)", bg: "rgba(148,163,184,.1)" }
          : failed ? LEVEL[c.level] : { t: "Passed", color: "var(--success-card)", bg: "rgba(16,185,129,.1)" };
        return (
          <div key={c.rule_id} className="cc-chk" style={compact ? { gridTemplateColumns: "minmax(0,1fr) 96px" } : undefined}>
            {!compact && <span>{c.label}</span>}
            <span style={{ color: "var(--text-secondary)" }}>{compact ? <><b style={{ color: "var(--text-primary)", fontWeight: 500 }}>{c.label}.</b> {c.detail}</> : c.detail}</span>
            <span className="cc-badge" style={{ color: tone.color, background: tone.bg, justifySelf: "end" }}><span className="cc-dot" />{tone.t}</span>
          </div>
        );
      })}
    </div>
  );
};

/** Counts per level for a stage's failed checks (Plan side panel). */
export const ChecksSummary: React.FC<{ checks: CheckResult[] }> = ({ checks }) => {
  const count = (l: Level) => checks.filter(c => c.status === "fail" && c.level === l).length;
  const passed = checks.filter(c => c.status === "pass").length;
  return (
    <div className="cc-card">
      <span style={{ fontWeight: 600, fontSize: 13.5 }}>Guardrail check</span>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}><span style={{ color: "var(--success-card)" }}>● Passed</span><span className="cc-mono">{passed}</span></div>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}><span style={{ color: LEVEL.guard.color }}>● Guardrail</span><span className="cc-mono">{count("guard")}</span></div>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}><span style={{ color: LEVEL.block.color }}>● Blocker</span><span className="cc-mono">{count("block")}</span></div>
      {count("rec") > 0 && <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}><span style={{ color: LEVEL.rec.color }}>● Recommendation</span><span className="cc-mono">{count("rec")}</span></div>}
    </div>
  );
};
